// @ts-nocheck
import * as React from "react";
import * as V from "../api";
import {
  Badge, Button, Card, Empty, Field, Input, Loading, Modal, Select, TOKENS, Textarea,
  errorMessage, useToast,
} from "../ui";

// Phase 3 — 민보(民保) 자율 협력 안전망(아젠다 ⑤).
//   본인 관찰 등록/pause/resume/end  · 운영자 Youth 관찰 승인.

export function SafetyWatchesPanel({ isOperator }: { isOperator: boolean }) {
  const toast = useToast();
  const [status, setStatus] = React.useState("");
  const [list, setList] = React.useState<V.SafetyWatch[] | null>(null);
  const [creating, setCreating] = React.useState(false);

  const reload = React.useCallback(() => {
    V.safetyWatches.list({ status: status || undefined })
      .then((r) => setList(r.items))
      .catch((e) => { setList([]); toast.push(errorMessage(e), "danger"); });
  }, [status, toast]);
  React.useEffect(() => { reload(); }, [reload]);

  const act = async (fn: () => Promise<any>, msg: string) => {
    try { await fn(); toast.push(msg, "success"); reload(); }
    catch (e: any) { toast.push(errorMessage(e), "danger"); }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
          민보 안전망
          <span style={{ fontSize: 12, color: TOKENS.ink3, marginLeft: 8, fontWeight: 500 }}>
            {isOperator ? "(전체 조회)" : "(내 관찰만)"}
          </span>
        </h2>
        <div style={{ display: "flex", gap: 6 }}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 120 }}>
            <option value="">전체</option>
            {Object.entries(V.LABELS.watchStatus).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </Select>
          <Button onClick={() => setCreating(true)}>관찰 등록</Button>
        </div>
      </div>

      {list === null ? <Loading /> :
       list.length === 0 ? <Empty title="등록된 관찰이 없습니다." /> :
        <div style={{ display: "grid", gap: 8 }}>
          {list.map((w) => (
            <Card key={w.id}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {V.LABELS.watchable[w.watchable_type]} #{w.watchable_id}
                    &nbsp;<Badge tone={w.status === "active" ? "success" : w.status === "paused" ? "warning" : "neutral"}>
                      {V.LABELS.watchStatus[w.status]}
                    </Badge>
                    &nbsp;<Badge tone={w.severity_hint === "high" ? "danger" : "info"}>
                      {V.LABELS.severity[w.severity_hint]}
                    </Badge>
                    {w.watchable_type === "Youth" && !w.approved_at && (
                      <>&nbsp;<Badge tone="warning">승인 대기</Badge></>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: TOKENS.ink3, marginTop: 2 }}>
                    {w.user.name} 등록 · {fmt(w.created_at)}
                    {w.note && ` · "${w.note}"`}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  {w.status === "active" && (
                    <Button variant="secondary" size="sm"
                      onClick={() => act(() => V.safetyWatches.pause(w.id), "일시중지했습니다.")}>일시중지</Button>
                  )}
                  {w.status === "paused" && (
                    <Button variant="secondary" size="sm"
                      onClick={() => act(() => V.safetyWatches.resume(w.id), "재개했습니다.")}>재개</Button>
                  )}
                  {w.status !== "ended" && (
                    <Button variant="ghost" size="sm"
                      onClick={() => {
                        if (!window.confirm("관찰을 종료하시겠습니까?")) return;
                        act(() => V.safetyWatches.end(w.id), "종료했습니다.");
                      }}>종료</Button>
                  )}
                  {isOperator && w.watchable_type === "Youth" && !w.approved_at && (
                    <Button size="sm"
                      onClick={() => act(() => V.safetyWatches.approve(w.id), "운영자 승인 완료.")}>승인</Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      }

      <CreateWatchModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => { setCreating(false); reload(); }}
      />
    </div>
  );
}

function CreateWatchModal({ open, onClose, onCreated }: {
  open: boolean; onClose: () => void; onCreated: () => void;
}) {
  const toast = useToast();
  const [type, setType] = React.useState("Youth");
  const [id, setId] = React.useState("");
  const [severity, setSeverity] = React.useState("normal");
  const [note, setNote] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    setType("Youth"); setId(""); setSeverity("normal"); setNote(""); setSubmitting(false);
  }, [open]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await V.safetyWatches.create({
        watchable_type: type,
        watchable_id: Number(id),
        severity_hint: severity,
        note: note || undefined,
      });
      toast.push("관찰을 등록했습니다." + (type === "Youth" ? " 운영자 승인 후 민감 알림이 활성화됩니다." : ""), "success");
      onCreated();
    } catch (e: any) {
      toast.push(errorMessage(e), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title="민보 관찰 등록"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>취소</Button>
          <Button onClick={submit} disabled={submitting || !id}>{submitting ? "등록 중…" : "등록"}</Button>
        </>
      }
    >
      <Field label="관찰 대상 유형">
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="Youth">청소년</option>
          <option value="Region">권역</option>
          <option value="VillageProgram">프로그램</option>
        </Select>
      </Field>
      <Field label="대상 ID" required
             hint="청소년/권역/프로그램의 숫자 ID. (리스트에서 복사 전환 UI는 Phase 4+ 예정)">
        <Input value={id} onChange={(e) => setId(e.target.value)} />
      </Field>
      <Field label="민감도">
        <Select value={severity} onChange={(e) => setSeverity(e.target.value)}>
          <option value="low">낮음</option>
          <option value="normal">보통</option>
          <option value="high">주의</option>
        </Select>
      </Field>
      <Field label="메모">
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="예: 등하교 길 관찰" />
      </Field>
    </Modal>
  );
}

function fmt(iso: string) { try { return new Date(iso).toLocaleDateString("ko-KR"); } catch { return iso; } }
